/*
 * Analytics — aggregations.
 *
 * Pure functions. Each takes raw `AnalyticsRow[]` (rows from the admin
 * submissions endpoint) and computes the slice the dashboard needs.
 *
 * Tolerant: payloads that don't match the expected tool shape are
 * skipped, not thrown. The dashboard renders empty states when a
 * section has no data.
 *
 * Mirrors the prototype's logic in
 * artifacts/prototypes/due-diligence-workbench-analytics.html (the
 * `ddwSubmissions()` / `vendorList()` / `distribution()` helpers).
 */

import type {
  AnalyticsRow,
  CountedItem,
  DdwPayload,
  DdwVendorPayload,
  OwnershipAggregate,
  PlanBuilderPayload,
  ResearchEntry,
  StayOrGoPayload,
  VendorAggregate,
} from './types';

// ─── Tool filtering ────────────────────────────────────────────────

export function planRows(rows: AnalyticsRow[]): AnalyticsRow[] {
  return rows.filter((r) => r.tool_id === 'plan_builder');
}

export function ddwRows(rows: AnalyticsRow[]): AnalyticsRow[] {
  return rows.filter((r) => r.tool_id === 'ddw');
}

export function stayOrGoRows(rows: AnalyticsRow[]): AnalyticsRow[] {
  return rows.filter((r) => r.tool_id === 'stay_or_go');
}

// ─── Tool-specific payload accessors ───────────────────────────────

function asObj(payload: unknown): Record<string, unknown> | null {
  return payload && typeof payload === 'object' && !Array.isArray(payload)
    ? (payload as Record<string, unknown>)
    : null;
}

export function planPayload(row: AnalyticsRow): PlanBuilderPayload | null {
  if (row.tool_id !== 'plan_builder') return null;
  return asObj(row.payload) as PlanBuilderPayload | null;
}

export function ddwPayload(row: AnalyticsRow): DdwPayload | null {
  if (row.tool_id !== 'ddw') return null;
  return asObj(row.payload) as DdwPayload | null;
}

export function sgPayload(row: AnalyticsRow): StayOrGoPayload | null {
  if (row.tool_id !== 'stay_or_go') return null;
  return asObj(row.payload) as StayOrGoPayload | null;
}

// ─── Combined lens accessors ──────────────────────────────────────
//
// The KPI strip + lens-pattern + red-flag sections aggregate across
// any tool whose payload carries a lens (Plan Builder + DDW). Stay or
// Go? is its own thing.

interface LensView {
  jobStatement?: string;
  frictionPhase?: string | null;
  flexibility?: string | null;
  feel?: string[];
  avoid?: string[];
  redFlagWordsHit?: string[];
}

export function lensFor(row: AnalyticsRow): LensView | null {
  // Prefer the projection-view computed values when present (those came
  // from the API's SQL projection — they're the canonical reading).
  // Fall back to walking the payload for anything missing.
  const payload = asObj(row.payload);
  const lens =
    payload && typeof payload.lens === 'object' && payload.lens !== null
      ? (payload.lens as Record<string, unknown>)
      : null;

  if (!lens && !row.computed) return null;

  const emotional =
    lens && typeof lens.emotional === 'object' && lens.emotional !== null
      ? (lens.emotional as Record<string, unknown>)
      : null;

  return {
    jobStatement: typeof lens?.jobStatement === 'string' ? lens.jobStatement : undefined,
    frictionPhase:
      row.computed?.friction_phase ??
      (typeof lens?.frictionPhase === 'string' ? lens.frictionPhase : null),
    flexibility:
      row.computed?.flexibility ??
      (typeof lens?.flexibility === 'string' ? lens.flexibility : null),
    feel: Array.isArray(emotional?.feel)
      ? (emotional!.feel as unknown[]).filter((s): s is string => typeof s === 'string')
      : undefined,
    avoid: Array.isArray(emotional?.avoid)
      ? (emotional!.avoid as unknown[]).filter((s): s is string => typeof s === 'string')
      : undefined,
    redFlagWordsHit: Array.isArray(lens?.redFlagWordsHit)
      ? (lens!.redFlagWordsHit as unknown[]).filter((s): s is string => typeof s === 'string')
      : undefined,
  };
}

// ─── KPI strip ────────────────────────────────────────────────────

export interface KpiSummary {
  total: number;
  planCount: number;
  ddwCount: number;
  stayOrGoCount: number;
  ddwScored1Plus: number;
  researchOptIns: number;
  lastSubmittedAt: string | null;
}

export function kpiSummary(rows: AnalyticsRow[]): KpiSummary {
  const ddw = ddwRows(rows);
  let scored = 0;
  let research = 0;
  for (const row of ddw) {
    const p = ddwPayload(row);
    if (!p) continue;
    if ((p.vendors ?? []).some((v) => v.scored)) scored++;
    const r = p.research;
    if (r && (hasResearchAnswer(r.q1) || hasResearchAnswer(r.q2) || hasResearchAnswer(r.q3))) {
      research++;
    }
  }
  let last: string | null = null;
  for (const row of rows) {
    if (!last || row.submitted_at > last) last = row.submitted_at;
  }
  return {
    total: rows.length,
    planCount: planRows(rows).length,
    ddwCount: ddw.length,
    stayOrGoCount: stayOrGoRows(rows).length,
    ddwScored1Plus: scored,
    researchOptIns: research,
    lastSubmittedAt: last,
  };
}

function hasResearchAnswer(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  if (value && typeof value === 'object') {
    const a = (value as { answer?: unknown }).answer;
    return typeof a === 'string' && a.trim().length > 0;
  }
  return false;
}

// ─── Cross-tool conversion ────────────────────────────────────────

export interface CrossToolSummary {
  planClients: number;
  ddwClients: number;
  bothClients: number;
  planOnly: number;
  ddwOnly: number;
  planToDdwRatePct: number;
}

export function crossToolSummary(rows: AnalyticsRow[]): CrossToolSummary {
  const planClients = new Set(planRows(rows).map((r) => r.client_id));
  const ddwClients = new Set(ddwRows(rows).map((r) => r.client_id));
  let both = 0;
  for (const id of planClients) {
    if (ddwClients.has(id)) both++;
  }
  return {
    planClients: planClients.size,
    ddwClients: ddwClients.size,
    bothClients: both,
    planOnly: planClients.size - both,
    ddwOnly: ddwClients.size - both,
    planToDdwRatePct: planClients.size > 0 ? Math.round((both / planClients.size) * 100) : 0,
  };
}

// ─── DDW funnel ───────────────────────────────────────────────────

export interface FunnelStage {
  label: string;
  count: number;
  pct: number;
}

export function ddwFunnel(rows: AnalyticsRow[]): FunnelStage[] {
  const ddw = ddwRows(rows);
  const total = ddw.length;
  if (total === 0) return [];
  const scoredCount = (n: number) =>
    ddw.filter((r) => {
      const p = ddwPayload(r);
      return ((p?.vendors ?? []).filter((v) => v.scored).length ?? 0) >= n;
    }).length;
  const stages = [
    { label: 'Phase 1 complete (Workbench submitted)', count: total },
    { label: 'Scored 1+ vendor', count: scoredCount(1) },
    { label: 'Scored 2+ vendors', count: scoredCount(2) },
    { label: 'Scored 3+ vendors', count: scoredCount(3) },
    { label: 'Scored 5+ vendors', count: scoredCount(5) },
  ];
  return stages.map((s) => ({ ...s, pct: total > 0 ? Math.round((s.count / total) * 100) : 0 }));
}

// ─── Distribution helper ──────────────────────────────────────────

export function distributionOf(
  rows: AnalyticsRow[],
  getter: (row: AnalyticsRow) => string | string[] | null | undefined,
): CountedItem[] {
  const map = new Map<string, number>();
  for (const row of rows) {
    const v = getter(row);
    if (v == null) continue;
    if (Array.isArray(v)) {
      for (const k of v) {
        if (typeof k === 'string' && k) map.set(k, (map.get(k) ?? 0) + 1);
      }
    } else if (typeof v === 'string' && v) {
      map.set(v, (map.get(v) ?? 0) + 1);
    }
  }
  return Array.from(map.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((a, b) => b.count - a.count);
}

// ─── Lens patterns ────────────────────────────────────────────────

export interface LensPatterns {
  friction: CountedItem[];
  flex: CountedItem[];
  feel: CountedItem[];
  avoid: CountedItem[];
}

export function lensPatterns(rows: AnalyticsRow[]): LensPatterns {
  // Lens patterns aggregate across Plan + DDW (both carry a lens).
  // Stay or Go? has its own structure, excluded.
  const lensRows = rows.filter((r) => r.tool_id !== 'stay_or_go');
  return {
    friction: distributionOf(lensRows, (r) => lensFor(r)?.frictionPhase),
    flex: distributionOf(lensRows, (r) => lensFor(r)?.flexibility),
    feel: distributionOf(lensRows, (r) => lensFor(r)?.feel),
    avoid: distributionOf(lensRows, (r) => lensFor(r)?.avoid),
  };
}

// ─── Red flag fire rate ───────────────────────────────────────────

export interface RedFlagSummary {
  total: number;
  fired: number;
  pct: number;
  topTriggers: CountedItem[];
}

export function redFlagSummary(rows: AnalyticsRow[]): RedFlagSummary {
  const lensRows = rows.filter((r) => r.tool_id !== 'stay_or_go');
  const total = lensRows.length;
  let fired = 0;
  for (const r of lensRows) {
    const hits = lensFor(r)?.redFlagWordsHit ?? [];
    if (hits.length > 0) fired++;
  }
  const triggers = distributionOf(lensRows, (r) => lensFor(r)?.redFlagWordsHit);
  return {
    total,
    fired,
    pct: total > 0 ? Math.round((fired / total) * 100) : 0,
    topTriggers: triggers.slice(0, 6),
  };
}

// ─── Vendor frequency ────────────────────────────────────────────

interface VendorEntry {
  row: AnalyticsRow;
  vendor: DdwVendorPayload;
}

export function scoredVendorEntries(rows: AnalyticsRow[]): VendorEntry[] {
  const out: VendorEntry[] = [];
  for (const row of ddwRows(rows)) {
    const p = ddwPayload(row);
    for (const v of p?.vendors ?? []) {
      if (v.scored) out.push({ row, vendor: v });
    }
  }
  return out;
}

function adoptionOf(v: DdwVendorPayload): number | null {
  if (typeof v.adoption_confidence === 'number') return v.adoption_confidence;
  if (typeof v.adoptionConfidence === 'number') return v.adoptionConfidence;
  return null;
}

export function vendorFrequency(rows: AnalyticsRow[]): VendorAggregate[] {
  const map = new Map<string, { count: number; gut: number[]; adopt: number[] }>();
  for (const { vendor } of scoredVendorEntries(rows)) {
    const name = (vendor.name ?? '').trim();
    if (!name) continue;
    let entry = map.get(name);
    if (!entry) {
      entry = { count: 0, gut: [], adopt: [] };
      map.set(name, entry);
    }
    entry.count++;
    if (typeof vendor.score?.gut === 'number') entry.gut.push(vendor.score.gut);
    const a = adoptionOf(vendor);
    if (a !== null) entry.adopt.push(a);
  }
  return Array.from(map.entries())
    .map(([name, e]) => ({
      name,
      count: e.count,
      avgGut: e.gut.length > 0 ? +(e.gut.reduce((a, b) => a + b, 0) / e.gut.length).toFixed(1) : null,
      avgAdoption:
        e.adopt.length > 0 ? Math.round(e.adopt.reduce((a, b) => a + b, 0) / e.adopt.length) : null,
    }))
    .sort((a, b) => b.count - a.count);
}

// ─── Risk distributions ──────────────────────────────────────────

export interface HistogramBin {
  label: string;
  min: number;
  max: number;
  count: number;
  tone?: 'error' | 'warn' | 'success';
}

const TAX_BINS: ReadonlyArray<Omit<HistogramBin, 'count'>> = [
  { label: '<$100K', min: 0, max: 100_000 },
  { label: '$100–250K', min: 100_000, max: 250_000 },
  { label: '$250–500K', min: 250_000, max: 500_000 },
  { label: '$500K+', min: 500_000, max: Number.POSITIVE_INFINITY },
];

const ADOPTION_BINS: ReadonlyArray<Omit<HistogramBin, 'count'>> = [
  { label: '<40%', min: 0, max: 40, tone: 'error' },
  { label: '40–70%', min: 40, max: 70, tone: 'warn' },
  { label: '70%+', min: 70, max: 101, tone: 'success' },
];

function histogram(values: number[], bins: ReadonlyArray<Omit<HistogramBin, 'count'>>): HistogramBin[] {
  return bins.map((b) => ({
    ...b,
    count: values.filter((v) => v >= b.min && v < b.max).length,
  }));
}

export interface RiskDistributions {
  tax: HistogramBin[];
  adoption: HistogramBin[];
}

export function riskDistributions(rows: AnalyticsRow[]): RiskDistributions {
  const entries = scoredVendorEntries(rows);
  const taxes = entries.map((e) => ddwPayload(e.row)?.lens?.tax?.calculatedAnnual ?? 0);
  const conf = entries.map((e) => adoptionOf(e.vendor) ?? 0);
  return {
    tax: histogram(taxes, TAX_BINS),
    adoption: histogram(conf, ADOPTION_BINS),
  };
}

// ─── Ownership breakdown ─────────────────────────────────────────

const OWNERSHIP_LABELS: Record<string, string> = {
  founder: 'Founder',
  public: 'Public',
  vc: 'VC-backed',
  pe: 'PE-owned',
  rollup: 'PE Roll-up',
  unknown: 'Unknown',
};

const OWNERSHIP_RISK_KEYS = new Set(['pe', 'rollup', 'unknown']);

export function ownershipBreakdown(rows: AnalyticsRow[]): OwnershipAggregate[] {
  const map = new Map<string, { count: number; adopt: number[] }>();
  for (const { vendor } of scoredVendorEntries(rows)) {
    const k = vendor.score?.ownership;
    if (!k) continue;
    let entry = map.get(k);
    if (!entry) {
      entry = { count: 0, adopt: [] };
      map.set(k, entry);
    }
    entry.count++;
    const a = adoptionOf(vendor);
    if (a !== null) entry.adopt.push(a);
  }
  return Array.from(map.entries())
    .map(([key, v]) => ({
      key,
      label: OWNERSHIP_LABELS[key] ?? key,
      count: v.count,
      avgAdoption:
        v.adopt.length > 0 ? Math.round(v.adopt.reduce((a, b) => a + b, 0) / v.adopt.length) : null,
    }))
    .sort((a, b) => b.count - a.count);
}

export function isOwnershipRisk(key: string): boolean {
  return OWNERSHIP_RISK_KEYS.has(key);
}

// ─── Research answers ────────────────────────────────────────────

const DEFAULT_RESEARCH_LABELS: Record<string, string> = {
  q1: 'What triggered this evaluation?',
  q2: "Who's on your decision team?",
  q3: "What's your timeline?",
};

export interface ResearchByQuestion {
  q1: { label: string; entries: ResearchEntry[] };
  q2: { label: string; entries: ResearchEntry[] };
  q3: { label: string; entries: ResearchEntry[] };
  totalOptIns: number;
}

export function researchByQuestion(rows: AnalyticsRow[]): ResearchByQuestion {
  const result: ResearchByQuestion = {
    q1: { label: DEFAULT_RESEARCH_LABELS.q1, entries: [] },
    q2: { label: DEFAULT_RESEARCH_LABELS.q2, entries: [] },
    q3: { label: DEFAULT_RESEARCH_LABELS.q3, entries: [] },
    totalOptIns: 0,
  };
  let optIns = 0;
  for (const row of ddwRows(rows)) {
    const p = ddwPayload(row);
    const r = p?.research;
    if (!r) continue;
    let any = false;
    for (const qk of ['q1', 'q2', 'q3'] as const) {
      const v = r[qk];
      let text = '';
      let label = result[qk].label;
      if (typeof v === 'string') {
        text = v;
      } else if (v && typeof v === 'object') {
        if (typeof v.answer === 'string') text = v.answer;
        if (typeof v.question === 'string' && v.question) {
          label = v.question;
          result[qk].label = v.question;
        }
      }
      if (text.trim()) {
        any = true;
        result[qk].entries.push({
          question: label,
          answer: text,
          date: row.submitted_at,
        });
      }
    }
    if (any) optIns++;
  }
  result.totalOptIns = optIns;
  return result;
}

// ─── Recent submissions ──────────────────────────────────────────

export interface RecentSubmissionRow {
  row: AnalyticsRow;
  toolLabel: string;
  toolTone: 'success' | 'warning' | 'primary' | 'neutral';
  summary: string;
  friction: string;
  flex: string;
  vendorsScored: number | null;
  hasResearch: boolean;
}

export function recentSubmissions(rows: AnalyticsRow[], n = 20): RecentSubmissionRow[] {
  const sorted = [...rows].sort((a, b) => b.submitted_at.localeCompare(a.submitted_at)).slice(0, n);
  return sorted.map((row) => {
    if (row.tool_id === 'plan_builder') {
      const p = planPayload(row);
      return {
        row,
        toolLabel: 'Plan',
        toolTone: 'primary' as const,
        summary: p?.lens?.jobStatement ?? '—',
        friction: p?.lens?.frictionPhase ?? row.computed?.friction_phase ?? '—',
        flex: p?.lens?.flexibility ?? row.computed?.flexibility ?? '—',
        vendorsScored: null,
        hasResearch: false,
      };
    }
    if (row.tool_id === 'ddw') {
      const p = ddwPayload(row);
      const scored = (p?.vendors ?? []).filter((v) => v.scored).length;
      const r = p?.research;
      const hasResearch = !!(
        r &&
        (hasResearchAnswer(r.q1) || hasResearchAnswer(r.q2) || hasResearchAnswer(r.q3))
      );
      return {
        row,
        toolLabel: 'Workbench',
        toolTone: 'success' as const,
        summary: p?.lens?.jobStatement ?? '—',
        friction: p?.lens?.frictionPhase ?? row.computed?.friction_phase ?? '—',
        flex: p?.lens?.flexibility ?? row.computed?.flexibility ?? '—',
        vendorsScored: scored,
        hasResearch,
      };
    }
    // stay_or_go
    const p = sgPayload(row);
    const score = p?.computed?.score;
    const verdict = p?.computed?.verdictLabel ?? '';
    return {
      row,
      toolLabel: 'Stay or Go?',
      toolTone: 'warning' as const,
      summary: `Stay Score ${score ?? '—'}/10 · ${verdict}`,
      friction: '—',
      flex: p?.tier ?? '—',
      vendorsScored: null,
      hasResearch: false,
    };
  });
}

// ─── Stay or Go? distribution ────────────────────────────────────

export interface StayOrGoSummary {
  total: number;
  byVerdict: CountedItem[];
  avgStayScore: number | null;
  avgCostOfStaying: number | null;
  byTier: CountedItem[];
}

export function stayOrGoSummary(rows: AnalyticsRow[]): StayOrGoSummary {
  const sg = stayOrGoRows(rows);
  if (sg.length === 0) {
    return { total: 0, byVerdict: [], avgStayScore: null, avgCostOfStaying: null, byTier: [] };
  }
  const verdicts = distributionOf(sg, (r) => sgPayload(r)?.computed?.verdictLabel ?? null);
  const tiers = distributionOf(sg, (r) => sgPayload(r)?.tier ?? null);
  const scores = sg
    .map((r) => sgPayload(r)?.computed?.score)
    .filter((n): n is number => typeof n === 'number');
  const costs = sg
    .map((r) => sgPayload(r)?.computed?.costOfStaying24mo)
    .filter((n): n is number => typeof n === 'number');
  return {
    total: sg.length,
    byVerdict: verdicts,
    avgStayScore:
      scores.length > 0 ? +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1) : null,
    avgCostOfStaying:
      costs.length > 0 ? Math.round(costs.reduce((a, b) => a + b, 0) / costs.length) : null,
    byTier: tiers,
  };
}
